import React, { useEffect, useState, useCallback } from "react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Hummingbird } from "../components/Hummingbird";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    Plus, LogOut, Pencil, Trash2, CalendarDays, Tag, Sparkles, ChevronRight, MessageCircle, Clock, CheckCircle2, ArrowLeft, ListChecks, Heart, Paperclip, File as FileIcon, Image as ImageIcon, FileText, Download, Upload, X, Mail,
} from "lucide-react";
import { EmailActivityDialog } from "../components/EmailActivityDialog";

const SHAWNA_EMAILS = ["shawnajordan11@gmail.com", "jordanshawna44@gmail.com"];

const ATTACHMENT_MAX_MB = 20;

const formatBytes = (n) => {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (ct) => typeof ct === "string" && ct.startsWith("image/");
const isPdf = (ct) => ct === "application/pdf";

const STATUS_META = {
    pending: { label: "Pending", color: "bg-[#57675F]/10 text-[#57675F]" },
    in_progress: { label: "In Progress", color: "bg-[#D97B48]/15 text-[#9C5429]" },
    completed: { label: "Completed", color: "bg-[#7A9E72]/15 text-[#4A6A47]" },
};

const fmtDate = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return null; }
};

const fmtDateTime = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return null; }
};

export default function Dashboard() {
    const { user, setUser, logout } = useAuth();
    const [goals, setGoals] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openTask, setOpenTask] = useState(null);
    const [showWelcome, setShowWelcome] = useState(false);

    const [goalDialog, setGoalDialog] = useState({ open: false, editing: null });
    const [taskDialog, setTaskDialog] = useState({ open: false, editing: null, goalId: null });
    const [selectedGoalId, setSelectedGoalId] = useState(null);
    const [emailActivityOpen, setEmailActivityOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const [g, t] = await Promise.all([api.get("/goals"), api.get("/tasks")]);
            setGoals(g.data);
            setTasks(t.data);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            let msg = "Could not load your flight plan";
            if (status) {
                msg += ` (HTTP ${status}${detail ? `: ${detail}` : ""})`;
            } else if (e?.code === "ECONNABORTED") {
                msg += " (timeout)";
            } else if (e?.message) {
                msg += ` (${e.message})`;
            }
            // eslint-disable-next-line no-console
            console.error("Flight plan load failed:", e);
            toast.error(msg, { duration: 12000 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Show first-login welcome popup for Shawna only, once per account.
    useEffect(() => {
        if (!user) return;
        if (SHAWNA_EMAILS.includes(user.email?.toLowerCase()) && !user.welcomed) {
            setShowWelcome(true);
        }
    }, [user]);

    const dismissWelcome = async () => {
        setShowWelcome(false);
        try {
            await api.post("/auth/welcomed");
            if (setUser) setUser({ ...user, welcomed: true });
        } catch (e) { /* non-blocking */ }
    };

    const tasksByGoal = (gid) => tasks.filter((t) => t.goal_id === gid).sort((a, b) => (a.priority - b.priority) || a.created_at.localeCompare(b.created_at));

    const overallProgress = tasks.length === 0 ? 0 : Math.round((tasks.filter((t) => t.completed).length / tasks.length) * 100);

    const toggleComplete = async (task) => {
        try {
            const r = await api.post(`/tasks/${task.task_id}/complete`);
            setTasks((prev) => prev.map((t) => (t.task_id === task.task_id ? r.data : t)));
            toast.success(r.data.completed ? "Marked complete" : "Marked incomplete");
        } catch (e) {
            toast.error("Could not update task");
        }
    };

    const deleteGoal = async (g) => {
        if (!window.confirm(`Delete goal "${g.title}" and all its tasks?`)) return;
        try {
            await api.delete(`/goals/${g.goal_id}`);
            setGoals((p) => p.filter((x) => x.goal_id !== g.goal_id));
            setTasks((p) => p.filter((x) => x.goal_id !== g.goal_id));
            if (selectedGoalId === g.goal_id) setSelectedGoalId(null);
            toast.success("Goal removed");
        } catch (e) { toast.error("Could not delete"); }
    };

    const deleteTask = async (t) => {
        if (!window.confirm(`Delete task "${t.title}"?`)) return;
        try {
            await api.delete(`/tasks/${t.task_id}`);
            setTasks((p) => p.filter((x) => x.task_id !== t.task_id));
            toast.success("Task removed");
        } catch (e) { toast.error("Could not delete"); }
    };

    return (
        <div className="min-h-screen bg-[#F9F8F3]" data-testid="dashboard-page">
            {/* Header */}
            <header className="sticky top-0 z-30 backdrop-blur bg-[#F9F8F3]/85 border-b border-[#E3E5DD]">
                <div className="max-w-6xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Hummingbird size={32} />
                        <div>
                            <div className="font-display font-semibold text-lg leading-none text-[#1E2623]">Flight Plan</div>
                            <div className="text-xs text-[#57675F] mt-0.5">Shawna&apos;s goals · prioritized</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {user?.is_admin && (
                            <>
                                <Badge className="bg-[#0F5C60] text-white hover:bg-[#0F5C60] rounded-full px-3 py-1 text-[10px] uppercase tracking-wider" data-testid="admin-badge">Admin</Badge>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEmailActivityOpen(true)}
                                    className="rounded-full text-[#0F5C60] hover:bg-[#0F5C60]/10"
                                    data-testid="open-email-activity-button"
                                    title="Email Activity"
                                >
                                    <Mail className="w-4 h-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Email</span>
                                </Button>
                            </>
                        )}
                        <div className="hidden sm:flex items-center gap-2">
                            {user?.picture ? (
                                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-[#E3E5DD]" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-[#0F5C60] text-white flex items-center justify-center text-sm font-medium">{user?.name?.[0]?.toUpperCase() || "?"}</div>
                            )}
                            <span className="text-sm text-[#1E2623] font-medium">{user?.name}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button" className="rounded-full">
                            <LogOut className="w-4 h-4 mr-2" /> Sign out
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
                {/* Progress hero */}
                <section className="rounded-3xl bg-white border border-[#E3E5DD] p-6 lg:p-8 shadow-[0_2px_12px_-4px_rgba(15,92,96,0.08)] relative overflow-hidden leaf-pattern">
                    <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <div>
                            <p className="text-xs uppercase tracking-[0.2em] text-[#0F5C60] font-medium mb-2">
                                Welcome back, {user?.name?.split(" ")[0] || "friend"}
                            </p>
                            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#1E2623] leading-tight">
                                {overallProgress === 100 && tasks.length > 0
                                    ? "Every wingbeat paid off."
                                    : tasks.length === 0
                                        ? "Your flight plan awaits."
                                        : "Keep flying — one task at a time."}
                            </h1>
                            <p className="text-[#57675F] mt-3 max-w-xl">
                                {tasks.filter((t) => t.completed).length} of {tasks.length} tasks complete across {goals.length} goal{goals.length === 1 ? "" : "s"}.
                            </p>
                        </div>
                        <div className="md:w-72">
                            <div className="flex items-center justify-between text-xs text-[#57675F] mb-2">
                                <span className="uppercase tracking-wider">Overall progress</span>
                                <span className="font-medium text-[#1E2623]">{overallProgress}%</span>
                            </div>
                            <Progress value={overallProgress} className="h-2 bg-[#E3E5DD]" data-testid="overall-progress" />
                        </div>
                    </div>
                </section>

                {/* Admin actions */}
                {user?.is_admin && (
                    <div className="mt-6 flex flex-wrap gap-3" data-testid="admin-actions">
                        <Button
                            onClick={() => setGoalDialog({ open: true, editing: null })}
                            className="bg-[#0F5C60] hover:bg-[#0A464A] text-white rounded-full px-5"
                            data-testid="add-goal-button"
                        >
                            <Plus className="w-4 h-4 mr-2" /> New Goal
                        </Button>
                        <Button
                            onClick={() => {
                                if (goals.length === 0) { toast.error("Create a goal first"); return; }
                                setTaskDialog({ open: true, editing: null, goalId: goals[0].goal_id });
                            }}
                            variant="outline"
                            className="rounded-full border-[#E3E5DD] hover:bg-[#F9F8F3]"
                            data-testid="add-task-button"
                        >
                            <Plus className="w-4 h-4 mr-2" /> New Task
                        </Button>
                    </div>
                )}

                {/* Goals overview OR selected Goal detail */}
                <section className="mt-8" data-testid="goals-section">
                    {loading ? (
                        <div className="text-center py-20 text-[#57675F]">Loading…</div>
                    ) : goals.length === 0 ? (
                        <EmptyState isAdmin={user?.is_admin} onAdd={() => setGoalDialog({ open: true, editing: null })} />
                    ) : selectedGoalId === null ? (
                        // ===== GOALS OVERVIEW =====
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="goals-grid">
                            {goals.map((g) => {
                                const gTasks = tasksByGoal(g.goal_id);
                                const done = gTasks.filter((t) => t.completed).length;
                                const pct = gTasks.length === 0 ? 0 : Math.round((done / gTasks.length) * 100);
                                return (
                                    <button
                                        key={g.goal_id}
                                        onClick={() => setSelectedGoalId(g.goal_id)}
                                        className="group text-left rounded-3xl bg-white border border-[#E3E5DD] p-6 lg:p-7 shadow-sm hover:shadow-md hover:border-[#0F5C60]/30 transition-all fade-up flex flex-col"
                                        data-testid={`goal-card-${g.goal_id}`}
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <Sparkles className="w-4 h-4 text-[#C0324F]" />
                                            <p className="text-[11px] uppercase tracking-[0.2em] text-[#57675F] font-medium">Goal</p>
                                        </div>
                                        <h2 className="font-display text-2xl font-semibold text-[#1E2623] leading-tight">{g.title}</h2>
                                        {g.description && (
                                            <p className="text-[#57675F] mt-2 text-sm leading-relaxed line-clamp-3">{g.description}</p>
                                        )}

                                        <div className="mt-5 flex items-center justify-between text-xs text-[#57675F]">
                                            <span className="inline-flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" /> {gTasks.length} task{gTasks.length === 1 ? "" : "s"}</span>
                                            <span className="font-medium text-[#1E2623]">{done}/{gTasks.length} done</span>
                                        </div>
                                        <Progress value={pct} className="h-1.5 bg-[#E3E5DD] mt-2" />

                                        <div className="mt-5 pt-4 border-t border-[#E3E5DD] flex items-center justify-between">
                                            <div className="flex gap-1">
                                                {user?.is_admin && (
                                                    <>
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={(e) => { e.stopPropagation(); setGoalDialog({ open: true, editing: g }); }}
                                                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setGoalDialog({ open: true, editing: g }); } }}
                                                            className="inline-flex items-center text-xs text-[#57675F] hover:text-[#0F5C60] rounded-full px-2 py-1 cursor-pointer"
                                                            data-testid={`edit-goal-${g.goal_id}`}
                                                        >
                                                            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                                                        </span>
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={(e) => { e.stopPropagation(); deleteGoal(g); }}
                                                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); deleteGoal(g); } }}
                                                            className="inline-flex items-center text-xs text-[#57675F] hover:text-[#C0324F] rounded-full px-2 py-1 cursor-pointer"
                                                            data-testid={`delete-goal-${g.goal_id}`}
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            <span className="inline-flex items-center text-sm font-medium text-[#0F5C60] group-hover:translate-x-0.5 transition-transform">
                                                View tasks <ChevronRight className="w-4 h-4 ml-1" />
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        // ===== SELECTED GOAL DETAIL =====
                        (() => {
                            const g = goals.find((x) => x.goal_id === selectedGoalId);
                            if (!g) {
                                return null;
                            }
                            const gTasks = tasksByGoal(g.goal_id);
                            const done = gTasks.filter((t) => t.completed).length;
                            const pct = gTasks.length === 0 ? 0 : Math.round((done / gTasks.length) * 100);
                            return (
                                <div className="space-y-6 fade-up" data-testid={`goal-detail-${g.goal_id}`}>
                                    <Button
                                        variant="ghost"
                                        onClick={() => setSelectedGoalId(null)}
                                        className="rounded-full text-[#57675F] hover:text-[#0F5C60] -ml-2"
                                        data-testid="back-to-goals"
                                    >
                                        <ArrowLeft className="w-4 h-4 mr-2" /> All goals
                                    </Button>

                                    <div className="rounded-3xl bg-white border border-[#E3E5DD] p-6 lg:p-7 shadow-sm">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3">
                                                    <Sparkles className="w-4 h-4 text-[#C0324F]" />
                                                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#57675F] font-medium">Goal</p>
                                                </div>
                                                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#1E2623] mt-1">{g.title}</h2>
                                                {g.description && <p className="text-[#57675F] mt-2 max-w-2xl">{g.description}</p>}
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs text-[#57675F] uppercase tracking-wider">Done</div>
                                                <div className="font-display text-xl font-semibold text-[#0F5C60]">{done}/{gTasks.length}</div>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <Progress value={pct} className="h-1.5 bg-[#E3E5DD]" />
                                        </div>

                                        {user?.is_admin && (
                                            <div className="mt-4 flex gap-2 flex-wrap">
                                                <Button size="sm" className="rounded-full bg-[#0F5C60] hover:bg-[#0A464A] text-white" onClick={() => setTaskDialog({ open: true, editing: null, goalId: g.goal_id })} data-testid={`add-task-in-goal-${g.goal_id}`}>
                                                    <Plus className="w-4 h-4 mr-1" /> Add Task
                                                </Button>
                                                <Button size="sm" variant="ghost" className="rounded-full text-[#57675F]" onClick={() => setGoalDialog({ open: true, editing: g })} data-testid={`edit-goal-detail-${g.goal_id}`}>
                                                    <Pencil className="w-4 h-4 mr-1" /> Edit Goal
                                                </Button>
                                                <Button size="sm" variant="ghost" className="rounded-full text-[#C0324F] hover:bg-[#C0324F]/10" onClick={() => { deleteGoal(g); setSelectedGoalId(null); }} data-testid={`delete-goal-detail-${g.goal_id}`}>
                                                    <Trash2 className="w-4 h-4 mr-1" /> Delete Goal
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-3">
                                        {gTasks.length === 0 ? (
                                            <div className="rounded-2xl border border-dashed border-[#E3E5DD] py-12 text-center text-[#57675F] bg-white">
                                                No tasks yet for this goal.
                                            </div>
                                        ) : (
                                            gTasks.map((t) => (
                                                <TaskRow
                                                    key={t.task_id}
                                                    task={t}
                                                    isAdmin={user?.is_admin}
                                                    onOpen={() => setOpenTask(t)}
                                                    onToggle={() => toggleComplete(t)}
                                                    onEdit={() => setTaskDialog({ open: true, editing: t, goalId: t.goal_id })}
                                                    onDelete={() => deleteTask(t)}
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })()
                    )}
                </section>
            </main>

            {/* Task Detail */}
            <TaskDetailDialog
                task={openTask}
                onClose={() => setOpenTask(null)}
                onTaskUpdate={(updated) => {
                    setTasks((p) => p.map((t) => (t.task_id === updated.task_id ? updated : t)));
                    setOpenTask((current) =>
                        current && current.task_id === updated.task_id ? updated : current
                    );
                }}
                isAdmin={user?.is_admin}
                user={user}
            />

            {/* Email Activity (admin) */}
            {user?.is_admin && (
                <EmailActivityDialog
                    open={emailActivityOpen}
                    onClose={() => setEmailActivityOpen(false)}
                    adminEmail={user?.email}
                />
            )}

            {/* Goal Dialog */}
            <GoalFormDialog
                open={goalDialog.open}
                editing={goalDialog.editing}
                onClose={() => setGoalDialog({ open: false, editing: null })}
                onSaved={(g, isNew) => {
                    setGoals((p) => (isNew ? [...p, g] : p.map((x) => (x.goal_id === g.goal_id ? g : x))));
                }}
            />

            {/* Task Dialog */}
            <TaskFormDialog
                open={taskDialog.open}
                editing={taskDialog.editing}
                defaultGoalId={taskDialog.goalId}
                goals={goals}
                onClose={() => setTaskDialog({ open: false, editing: null, goalId: null })}
                onSaved={(t, isNew) => {
                    setTasks((p) => (isNew ? [...p, t] : p.map((x) => (x.task_id === t.task_id ? t : x))));
                }}
            />

            {/* First-login Welcome (Shawna) */}
            <Dialog open={showWelcome} onOpenChange={(o) => !o && dismissWelcome()}>
                <DialogContent
                    className="max-w-lg rounded-3xl bg-white border-[#E3E5DD] p-0 overflow-hidden"
                    data-testid="shawna-welcome-dialog"
                >
                    <div className="relative bg-gradient-to-br from-[#0F5C60] to-[#0A464A] px-8 pt-8 pb-6 text-white overflow-hidden">
                        <div className="absolute -top-6 -right-6 opacity-20">
                            <Hummingbird size={140} color="#F9F8F3" accent="#C0324F" />
                        </div>
                        <div className="relative hummingbird-float inline-block mb-3">
                            <Hummingbird size={48} color="#F9F8F3" accent="#C0324F" />
                        </div>
                        <p className="text-[11px] uppercase tracking-[0.25em] text-white/70 font-medium">
                            A note for you, Shawna
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mt-2">
                            Welcome to your<br />Flight Plan.
                        </h2>
                    </div>
                    <div className="px-8 py-6 space-y-4">
                        <p className="text-[#1E2623] text-base leading-relaxed" data-testid="welcome-message">
                            Here are your first two goals. Each goal has tasks. These tasks are prioritized starting with <span className="font-semibold text-[#0F5C60]">#1</span>.
                        </p>
                        <p className="text-[#1E2623] text-base leading-relaxed">
                            Start with the <span className="font-semibold text-[#C0324F]">Just Life-ing</span> goal. Add progress comments to each task as you can.
                        </p>
                        <p className="text-[#1E2623] text-lg leading-relaxed font-display">
                            Let&apos;s DO THIS hot stuff!!
                            <span className="ml-2 inline-flex items-center gap-1 text-[#C0324F] font-medium">
                                XOXO <Heart className="w-4 h-4 fill-[#C0324F]" />
                            </span>
                        </p>
                    </div>
                    <DialogFooter className="px-8 pb-8 pt-2">
                        <Button
                            onClick={dismissWelcome}
                            className="w-full bg-[#0F5C60] hover:bg-[#0A464A] text-white rounded-full py-6 text-base font-medium"
                            data-testid="welcome-dismiss-button"
                        >
                            Let&apos;s go
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function EmptyState({ isAdmin, onAdd }) {
    return (
        <div className="text-center py-16 rounded-3xl border border-dashed border-[#E3E5DD] bg-white" data-testid="empty-state">
            <div className="inline-block hummingbird-float">
                <Hummingbird size={72} />
            </div>
            <h3 className="font-display text-2xl mt-4 text-[#1E2623]">No goals yet</h3>
            <p className="text-[#57675F] mt-2 max-w-sm mx-auto">
                {isAdmin ? "Create the first goal and start adding tasks." : "Your admin will add goals here soon."}
            </p>
            {isAdmin && (
                <Button onClick={onAdd} className="mt-6 bg-[#0F5C60] hover:bg-[#0A464A] text-white rounded-full" data-testid="empty-add-goal">
                    <Plus className="w-4 h-4 mr-2" /> Create your first goal
                </Button>
            )}
        </div>
    );
}

function TaskRow({ task, isAdmin, onOpen, onToggle, onEdit, onDelete }) {
    const status = STATUS_META[task.status] || STATUS_META.pending;
    return (
        <div
            className={`group rounded-2xl border border-[#E3E5DD] bg-white hover:bg-[#F9F8F3] transition-colors p-4 sm:p-5 flex items-center gap-4 ${task.completed ? "opacity-70" : ""}`}
            data-testid={`task-row-${task.task_id}`}
        >
            {/* Priority chip */}
            <div className="flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-[#0F5C60]/8 shrink-0">
                <span className="text-[10px] uppercase tracking-wider text-[#57675F]">P</span>
                <span className="font-display text-lg font-semibold text-[#0F5C60] leading-none">{task.priority}</span>
            </div>

            {/* Checkbox — any signed-in user can mark complete/incomplete */}
            <div className="shrink-0">
                <Checkbox
                    checked={task.completed}
                    onCheckedChange={() => onToggle()}
                    className="w-6 h-6 rounded-full border-2 border-[#0F5C60] data-[state=checked]:bg-[#7A9E72] data-[state=checked]:border-[#7A9E72]"
                    data-testid={`task-checkbox-${task.task_id}`}
                    aria-label="Mark complete"
                />
            </div>

            {/* Main */}
            <button onClick={onOpen} className="flex-1 text-left min-w-0" data-testid={`task-open-${task.task_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-display text-base sm:text-lg font-medium text-[#1E2623] ${task.completed ? "line-through text-[#57675F]" : ""}`}>
                        {task.title}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${status.color}`}>
                        {status.label}
                    </span>
                </div>
                <div className="mt-1.5 flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-[#57675F]">
                    {task.category && (
                        <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {task.category}</span>
                    )}
                    {task.due_date && (
                        <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {fmtDate(task.due_date)}</span>
                    )}
                    {task.attachments_count > 0 && (
                        <span
                            className="inline-flex items-center gap-1 text-[#0F5C60] font-medium"
                            title={`${task.attachments_count} attachment${task.attachments_count === 1 ? "" : "s"}`}
                            data-testid={`task-attachments-badge-${task.task_id}`}
                        >
                            <Paperclip className="w-3 h-3" /> {task.attachments_count}
                        </span>
                    )}
                    {task.description && (
                        <span className="truncate max-w-md">{task.description}</span>
                    )}
                </div>
            </button>

            <div className="flex items-center gap-1 shrink-0">
                {isAdmin && (
                    <>
                        <Button size="icon" variant="ghost" onClick={onEdit} className="rounded-full text-[#57675F] hover:text-[#0F5C60]" data-testid={`edit-task-${task.task_id}`}>
                            <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={onDelete} className="rounded-full text-[#57675F] hover:text-[#C0324F]" data-testid={`delete-task-${task.task_id}`}>
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </>
                )}
                <Button size="icon" variant="ghost" onClick={onOpen} className="rounded-full text-[#0F5C60]">
                    <ChevronRight className="w-5 h-5" />
                </Button>
            </div>
        </div>
    );
}

function TaskDetailDialog({ task, onClose, onTaskUpdate, isAdmin, user }) {
    const [updates, setUpdates] = useState([]);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!task) return;
        (async () => {
            try {
                const r = await api.get(`/tasks/${task.task_id}/updates`);
                setUpdates(r.data);
            } catch (e) { /* ignore */ }
        })();
    }, [task]);

    if (!task) return null;
    const status = STATUS_META[task.status] || STATUS_META.pending;

    const submit = async () => {
        if (!comment.trim()) return;
        setLoading(true);
        try {
            const r = await api.post(`/tasks/${task.task_id}/updates`, { comment: comment.trim() });
            setUpdates((p) => [r.data, ...p]);
            setComment("");
            // status may flip to in_progress
            const fresh = await api.get("/tasks").then((res) => res.data.find((x) => x.task_id === task.task_id));
            if (fresh) onTaskUpdate(fresh);
            toast.success("Progress noted");
        } catch (e) {
            toast.error("Could not post update");
        } finally { setLoading(false); }
    };

    const deleteUpdate = async (u) => {
        try {
            await api.delete(`/updates/${u.update_id}`);
            setUpdates((p) => p.filter((x) => x.update_id !== u.update_id));
        } catch (e) { toast.error("Could not delete"); }
    };

    const changeStatus = async (newStatus) => {
        try {
            const r = await api.patch(`/tasks/${task.task_id}`, { status: newStatus });
            onTaskUpdate(r.data);
        } catch (e) { toast.error("Could not update status"); }
    };

    return (
        <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl rounded-3xl bg-white border-[#E3E5DD] p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="task-detail-dialog">
                <div className="p-6 sm:p-8 border-b border-[#E3E5DD] bg-[#F9F8F3]">
                    <div className="flex items-start gap-4">
                        <div className="flex flex-col items-center justify-center w-14 h-14 rounded-2xl bg-[#0F5C60]/10 shrink-0">
                            <span className="text-[10px] uppercase tracking-wider text-[#57675F]">Priority</span>
                            <span className="font-display text-2xl font-semibold text-[#0F5C60] leading-none">{task.priority}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <DialogHeader className="text-left">
                                <DialogTitle className="font-display text-2xl text-[#1E2623]">{task.title}</DialogTitle>
                                <DialogDescription className="text-[#57675F]">{task.description || "No description"}</DialogDescription>
                            </DialogHeader>
                            <div className="mt-3 flex items-center flex-wrap gap-2 text-xs text-[#57675F]">
                                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${status.color}`}>{status.label}</span>
                                {task.category && <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {task.category}</span>}
                                {task.due_date && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Due {fmtDate(task.due_date)}</span>}
                                {task.completed_at && <span className="inline-flex items-center gap-1 text-[#4A6A47]"><CheckCircle2 className="w-3 h-3" /> Completed {fmtDate(task.completed_at)}{task.completed_by_name ? ` by ${task.completed_by_name}` : ""}</span>}
                            </div>
                            {isAdmin && (
                                <div className="mt-4 flex items-center gap-2">
                                    <Label className="text-xs text-[#57675F] uppercase tracking-wider">Set status</Label>
                                    <Select value={task.status} onValueChange={changeStatus}>
                                        <SelectTrigger className="w-44 rounded-full" data-testid="status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="pending">Pending</SelectItem>
                                            <SelectItem value="in_progress">In Progress</SelectItem>
                                            <SelectItem value="completed">Completed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-6 sm:p-8 flex-1 overflow-y-auto">
                    <div className="flex items-center gap-2 mb-4">
                        <MessageCircle className="w-4 h-4 text-[#0F5C60]" />
                        <h3 className="font-display text-lg font-medium text-[#1E2623]">Progress Updates</h3>
                    </div>

                    <div className="space-y-3 mb-6" data-testid="updates-timeline">
                        {updates.length === 0 ? (
                            <p className="text-sm text-[#57675F]">No updates yet — share the first one below.</p>
                        ) : updates.map((u) => (
                            <div key={u.update_id} className="rounded-2xl border border-[#E3E5DD] bg-white p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-full bg-[#0F5C60]/10 text-[#0F5C60] flex items-center justify-center text-xs font-medium">
                                            {u.user_name?.[0]?.toUpperCase() || "?"}
                                        </div>
                                        <div className="text-sm font-medium text-[#1E2623]">{u.user_name}</div>
                                        <div className="text-xs text-[#57675F] flex items-center gap-1">
                                            <Clock className="w-3 h-3" /> {fmtDateTime(u.created_at)}
                                        </div>
                                    </div>
                                    {(user?.user_id === u.user_id || user?.is_admin) && (
                                        <Button size="icon" variant="ghost" className="rounded-full h-7 w-7 text-[#57675F] hover:text-[#C0324F]" onClick={() => deleteUpdate(u)} data-testid={`delete-update-${u.update_id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                </div>
                                <p className="mt-2 text-[#1E2623] whitespace-pre-wrap">{u.comment}</p>
                            </div>
                        ))}
                    </div>

                    <AttachmentsSection
                        taskId={task.task_id}
                        user={user}
                        onCountChange={(n) => onTaskUpdate({ ...task, attachments_count: n })}
                    />

                    <div className="rounded-2xl border border-[#E3E5DD] bg-[#F9F8F3] p-4" data-testid="add-update-form">
                        <Label className="text-xs uppercase tracking-wider text-[#57675F]">Post an update</Label>
                        <Textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="What progress have you made today?"
                            className="mt-2 bg-white rounded-xl border-[#E3E5DD]"
                            data-testid="update-comment-input"
                        />
                        <div className="mt-3 flex justify-end">
                            <Button onClick={submit} disabled={loading || !comment.trim()} className="bg-[#0F5C60] hover:bg-[#0A464A] text-white rounded-full" data-testid="submit-update-button">
                                Post update
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AttachmentsSection({ taskId, user, onCountChange }) {
    const [items, setItems] = React.useState([]);
    const [uploading, setUploading] = React.useState(false);
    const [error, setError] = React.useState("");
    const inputRef = React.useRef(null);

    const updateItems = React.useCallback((next) => {
        setItems(next);
        if (onCountChange) onCountChange(next.length);
    }, [onCountChange]);

    const reload = React.useCallback(async () => {
        try {
            const r = await api.get(`/tasks/${taskId}/attachments`);
            updateItems(r.data);
        } catch (e) {
            /* non-blocking */
        }
    }, [taskId, updateItems]);

    React.useEffect(() => { reload(); }, [reload]);

    const onPick = () => inputRef.current?.click();

    const onChange = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        setError("");
        for (const f of files) {
            if (f.size > ATTACHMENT_MAX_MB * 1024 * 1024) {
                setError(`${f.name} is over ${ATTACHMENT_MAX_MB} MB and was skipped.`);
                continue;
            }
            const fd = new FormData();
            fd.append("file", f);
            setUploading(true);
            try {
                const r = await api.post(`/tasks/${taskId}/attachments`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 120000,
                });
                updateItems([r.data, ...items]);
                toast.success(`Uploaded ${f.name}`);
            } catch (err) {
                const detail = err?.response?.data?.detail || err?.message || "upload failed";
                toast.error(`Could not upload ${f.name} (${detail})`);
            } finally {
                setUploading(false);
            }
        }
    };

    const remove = async (att) => {
        if (!window.confirm(`Remove attachment "${att.original_filename}"?`)) return;
        try {
            await api.delete(`/attachments/${att.attachment_id}`);
            updateItems(items.filter((x) => x.attachment_id !== att.attachment_id));
            toast.success("Attachment removed");
        } catch (e) {
            toast.error("Could not remove");
        }
    };

    return (
        <div className="mb-6" data-testid="attachments-section">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-[#0F5C60]" />
                    <h3 className="font-display text-lg font-medium text-[#1E2623]">
                        Attachments {items.length > 0 && (
                            <span className="text-sm text-[#57675F] font-normal">({items.length})</span>
                        )}
                    </h3>
                </div>
                <Button
                    type="button"
                    size="sm"
                    onClick={onPick}
                    disabled={uploading}
                    className="bg-[#0F5C60] hover:bg-[#0A464A] text-white rounded-full"
                    data-testid="attachment-upload-button"
                >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? "Uploading…" : "Add file"}
                </Button>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    onChange={onChange}
                    className="hidden"
                    data-testid="attachment-file-input"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                />
            </div>

            {error && (
                <div className="mb-3 text-xs text-[#C0324F] bg-[#C0324F]/8 border border-[#C0324F]/20 rounded-xl px-3 py-2">
                    {error}
                </div>
            )}

            {items.length === 0 ? (
                <p className="text-sm text-[#57675F]">No files attached yet — drop an image, PDF, or document above.</p>
            ) : (
                <div className="space-y-2">
                    {items.map((att) => (
                        <AttachmentRow key={att.attachment_id} att={att} canDelete={user?.is_admin || user?.user_id === att.uploaded_by_id} onDelete={() => remove(att)} />
                    ))}
                </div>
            )}
        </div>
    );
}

function AttachmentRow({ att, canDelete, onDelete }) {
    const viewHref = `${API}/attachments/${att.attachment_id}/download`;
    const downloadHref = `${API}/attachments/${att.attachment_id}/download?download=1`;
    const fmtWhen = (iso) => {
        try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
        catch { return ""; }
    };
    const Icon = isImage(att.content_type) ? ImageIcon : (isPdf(att.content_type) ? FileText : FileIcon);
    return (
        <div className="rounded-2xl border border-[#E3E5DD] bg-white p-3 flex items-center gap-3" data-testid={`attachment-${att.attachment_id}`}>
            {isImage(att.content_type) ? (
                <a href={viewHref} target="_blank" rel="noreferrer" className="shrink-0">
                    <img
                        src={viewHref}
                        alt={att.original_filename}
                        className="w-14 h-14 object-cover rounded-xl border border-[#E3E5DD] bg-[#F9F8F3]"
                        loading="lazy"
                    />
                </a>
            ) : (
                <div className="w-14 h-14 rounded-xl bg-[#0F5C60]/8 text-[#0F5C60] flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <a
                    href={viewHref}
                    target="_blank"
                    rel="noreferrer"
                    className="block font-medium text-[#1E2623] hover:text-[#0F5C60] truncate"
                    data-testid={`attachment-link-${att.attachment_id}`}
                >
                    {att.original_filename}
                </a>
                <div className="text-xs text-[#57675F] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span>{formatBytes(att.size)}</span>
                    <span>by {att.uploaded_by_name || att.uploaded_by_email}</span>
                    <span>{fmtWhen(att.created_at)}</span>
                </div>
            </div>
            <a
                href={downloadHref}
                download={att.original_filename}
                className="rounded-full p-2 text-[#57675F] hover:text-[#0F5C60] hover:bg-[#0F5C60]/8"
                title="Download"
                data-testid={`attachment-download-${att.attachment_id}`}
            >
                <Download className="w-4 h-4" />
            </a>
            {canDelete && (
                <button
                    onClick={onDelete}
                    className="rounded-full p-2 text-[#57675F] hover:text-[#C0324F] hover:bg-[#C0324F]/8"
                    title="Remove"
                    data-testid={`attachment-delete-${att.attachment_id}`}
                >
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

function GoalFormDialog({ open, editing, onClose, onSaved }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setTitle(editing?.title || "");
            setDescription(editing?.description || "");
        }
    }, [open, editing]);

    const save = async () => {
        if (!title.trim()) { toast.error("Title required"); return; }
        setSaving(true);
        try {
            if (editing) {
                const r = await api.patch(`/goals/${editing.goal_id}`, { title, description });
                onSaved(r.data, false);
                toast.success("Goal updated");
            } else {
                const r = await api.post("/goals", { title, description });
                onSaved(r.data, true);
                toast.success("Goal created");
            }
            onClose();
        } catch (e) { toast.error("Could not save goal"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="rounded-3xl max-w-md" data-testid="goal-form-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl text-[#1E2623]">{editing ? "Edit Goal" : "New Goal"}</DialogTitle>
                    <DialogDescription className="text-[#57675F]">A goal groups related tasks. e.g., "Finish Degree".</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[#57675F]">Title</Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 rounded-xl" data-testid="goal-title-input" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[#57675F]">Description (optional)</Label>
                        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 rounded-xl" data-testid="goal-description-input" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="bg-[#0F5C60] hover:bg-[#0A464A] text-white rounded-full" data-testid="save-goal-button">
                        {editing ? "Save changes" : "Create goal"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function TaskFormDialog({ open, editing, defaultGoalId, goals, onClose, onSaved }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState(1);
    const [category, setCategory] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [goalId, setGoalId] = useState("");
    const [status, setStatus] = useState("pending");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setTitle(editing?.title || "");
            setDescription(editing?.description || "");
            setPriority(editing?.priority || 1);
            setCategory(editing?.category || "");
            setDueDate(editing?.due_date ? editing.due_date.slice(0, 10) : "");
            setGoalId(editing?.goal_id || defaultGoalId || (goals[0]?.goal_id ?? ""));
            setStatus(editing?.status || "pending");
        }
    }, [open, editing, defaultGoalId, goals]);

    const save = async () => {
        if (!title.trim()) { toast.error("Title required"); return; }
        if (!goalId) { toast.error("Select a goal"); return; }
        setSaving(true);
        try {
            const payload = {
                goal_id: goalId,
                title,
                description,
                priority: Number(priority) || 1,
                category,
                due_date: dueDate ? new Date(dueDate).toISOString() : null,
            };
            if (editing) {
                const r = await api.patch(`/tasks/${editing.task_id}`, { ...payload, status });
                onSaved(r.data, false);
                toast.success("Task updated");
            } else {
                const r = await api.post("/tasks", payload);
                onSaved(r.data, true);
                toast.success("Task added");
            }
            onClose();
        } catch (e) {
            toast.error("Could not save task");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="rounded-3xl max-w-lg" data-testid="task-form-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl text-[#1E2623]">{editing ? "Edit Task" : "New Task"}</DialogTitle>
                    <DialogDescription className="text-[#57675F]">Describe a step toward the goal.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[#57675F]">Goal</Label>
                        <Select value={goalId} onValueChange={setGoalId}>
                            <SelectTrigger className="mt-1 rounded-xl" data-testid="task-goal-select"><SelectValue placeholder="Choose a goal" /></SelectTrigger>
                            <SelectContent>
                                {goals.map((g) => <SelectItem key={g.goal_id} value={g.goal_id}>{g.title}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[#57675F]">Title</Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 rounded-xl" data-testid="task-title-input" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[#57675F]">Description</Label>
                        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 rounded-xl" data-testid="task-description-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#57675F]">Priority (1 = top)</Label>
                            <Input type="number" min={1} value={priority} onChange={(e) => setPriority(e.target.value)} className="mt-1 rounded-xl" data-testid="task-priority-input" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#57675F]">Due date</Label>
                            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1 rounded-xl" data-testid="task-due-input" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[#57675F]">Category</Label>
                        <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g., Education, Health" className="mt-1 rounded-xl" data-testid="task-category-input" />
                    </div>
                    {editing && (
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#57675F]">Status</Label>
                            <Select value={status} onValueChange={setStatus}>
                                <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="bg-[#0F5C60] hover:bg-[#0A464A] text-white rounded-full" data-testid="save-task-button">
                        {editing ? "Save changes" : "Create task"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
